"use client";

// Main result card and significance card shown after the API response.

import Link from "next/link";
import type { KundaliResponse } from "@/lib/api";
import type { NAKSHATRA_DATA } from "../_data";
import Explainable from "@ishvaram/ui/Explainable";
import { useExplainableHandlers, useExplainer } from "@/hooks/useExplainer";

type NakshatraInfo = (typeof NAKSHATRA_DATA)[string];

interface Props {
  result: KundaliResponse;
  nakshatraInfo: NakshatraInfo | null;
  moonPada: number | undefined;
  isHindi: boolean;
  onReset: () => void;
}

const NAKSHATRA_HAS_EXPLAINER = new Set([
  "ashwini",
  "bharani",
  "krittika",
  "rohini",
  "mrigashira",
  "ardra",
  "punarvasu",
  "pushya",
  "ashlesha",
  "magha",
  "purva_phalguni",
  "uttara_phalguni",
  "hasta",
  "chitra",
  "swati",
  "vishakha",
  "anuradha",
  "jyeshtha",
  "moola",
  "purva_ashadha",
  "uttara_ashadha",
  "shravana",
  "dhanishta",
  "shatabhisha",
  "purva_bhadrapada",
  "uttara_bhadrapada",
  "revati",
]);

const RASHI_HAS_EXPLAINER = new Set([
  "mesh",
  "vrishabh",
  "mithun",
  "kark",
  "singh",
  "kanya",
  "tula",
  "vrishchik",
  "dhanu",
  "makar",
  "kumbh",
  "meen",
]);

export function NakshatraResultCard({ result, nakshatraInfo, moonPada, isHindi, onReset }: Props) {
  const nakshatraHandlers = useExplainableHandlers("nakshatra_calculator_result");
  const nakshatraId = result.moon_nakshatra;
  const hasNakshatraExplainer = NAKSHATRA_HAS_EXPLAINER.has(nakshatraId);
  const {
    data: nakshatraData,
    isLoading: nakshatraLoading,
    error: nakshatraError,
    refetch: nakshatraRefetch,
  } = useExplainer("nakshatra", nakshatraId, "medium", { enabled: false });

  const rashiId = result.moon_rashi;
  const hasRashiExplainer = RASHI_HAS_EXPLAINER.has(rashiId);
  const {
    data: rashiData,
    isLoading: rashiLoading,
    error: rashiError,
    refetch: rashiRefetch,
  } = useExplainer("rashi", rashiId, "medium", { enabled: false });

  return (
    <div className="space-y-6">
      {/* Main Result Card */}
      <div className="card-sacred p-6 sm:p-8 text-center">
        <span className="text-4xl block mb-3">&#x2606;</span>
        <h2
          className="text-[28px] font-bold capitalize"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--ish-dark)",
          }}
        >
          {hasNakshatraExplainer ? (
            <Explainable
              type="nakshatra"
              id={nakshatraId}
              intensity="medium"
              triggerLabel={nakshatraId.replace(/_/g, " ")}
              data={nakshatraData}
              loading={nakshatraLoading}
              error={nakshatraError ?? null}
              onRequestData={() => void nakshatraRefetch()}
              {...nakshatraHandlers}
              className="text-[28px] font-bold capitalize"
            />
          ) : (
            nakshatraId.replace(/_/g, " ")
          )}
        </h2>
        {nakshatraInfo && (
          <p className="text-[18px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
            {nakshatraInfo.hi}
          </p>
        )}

        <div className="divider-warm my-5" />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="block text-[12px] mb-1" style={{ color: "var(--ish-text-faint)" }}>
              Pada
            </span>
            <span className="font-semibold text-[14px]" style={{ color: "var(--ish-dark)" }}>
              {moonPada ?? "-"}
            </span>
          </div>
          <div>
            <span className="block text-[12px] mb-1" style={{ color: "var(--ish-text-faint)" }}>
              Ruling Planet
            </span>
            <span className="font-semibold text-[14px]" style={{ color: "var(--ish-dark)" }}>
              {nakshatraInfo?.ruler ?? "-"}
            </span>
          </div>
          <div>
            <span className="block text-[12px] mb-1" style={{ color: "var(--ish-text-faint)" }}>
              Deity
            </span>
            <span className="font-semibold text-[14px]" style={{ color: "var(--ish-dark)" }}>
              {nakshatraInfo?.deity ?? "-"}
            </span>
          </div>
          <div>
            <span className="block text-[12px] mb-1" style={{ color: "var(--ish-text-faint)" }}>
              Moon Sign
            </span>
            {hasRashiExplainer ? (
              <Explainable
                type="rashi"
                id={rashiId}
                intensity="medium"
                triggerLabel={rashiId.charAt(0).toUpperCase() + rashiId.slice(1)}
                data={rashiData}
                loading={rashiLoading}
                error={rashiError ?? null}
                onRequestData={() => void rashiRefetch()}
                {...nakshatraHandlers}
                className="font-semibold text-[14px] capitalize"
              />
            ) : (
              <span
                className="font-semibold text-[14px] capitalize"
                style={{ color: "var(--ish-dark)" }}
              >
                {rashiId}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Significance Card */}
      {nakshatraInfo && (
        <div className="card-sacred p-6">
          <h3
            className="text-[16px] font-bold mb-2"
            style={{
              fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
              color: "var(--ish-dark)",
            }}
          >
            Nakshatra Significance
          </h3>
          <p className="text-[14px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
            {nakshatraInfo.meaning}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          href="/free-kundali"
          className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
        >
          {isHindi ? "पूरी कुंडली पढ़ें →" : "Get Full Kundali Reading →"}
        </Link>
        <button
          onClick={onReset}
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200"
          style={{
            color: "var(--ish-text-muted)",
            border: "1px solid var(--ish-border)",
          }}
        >
          {isHindi ? "फिर से गणना करें" : "Calculate Again"}
        </button>
      </div>
    </div>
  );
}
